import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';

const ProjectApproach = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { t, language } = useLanguage();

  const stepsData = {
    en: [
      { number: '01', title: 'Consultation', subtitle: 'Consultation', description: 'Initial meeting to understand your vision, evaluate the space, and establish project expectations.' },
      { number: '02', title: 'Design', subtitle: 'Design', description: 'Development of detailed proposal with renders, materials, and structured timeline.' },
      { number: '03', title: 'Execution', subtitle: 'Execution', description: 'Implementation with continuous supervision, quality control, and transparent communication.' },
      { number: '04', title: 'Delivery', subtitle: 'Delivery', description: 'Final inspection, standards verification, and formal closure with satisfaction guarantee.' },
    ],
    es: [
      { number: '01', title: 'Consulta', subtitle: 'Consultation', description: 'Reunión inicial para comprender su visión, evaluar el espacio y establecer expectativas del proyecto.' },
      { number: '02', title: 'Diseño', subtitle: 'Design', description: 'Desarrollo de propuesta detallada con renders, materiales y cronograma estructurado.' },
      { number: '03', title: 'Ejecución', subtitle: 'Execution', description: 'Implementación con supervisión continua, control de calidad y comunicación transparente.' },
      { number: '04', title: 'Entrega', subtitle: 'Delivery', description: 'Inspección final, verificación de estándares y cierre formal con garantía de satisfacción.' },
    ],
  };

  const steps = stepsData[language];

  return (
    <section ref={ref} className="py-32 bg-[#0F0F0F] relative overflow-hidden">
      {/* Decorative lines */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="text-center mb-24"
        >
          <motion.span 
            className="font-montserrat font-light text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {t('process.tagline')}
          </motion.span>
          <h2 className="font-montserrat font-extralight text-4xl md:text-6xl text-white tracking-[0.1em] uppercase">
            {t('process.title')}
          </h2>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
          {steps.map((step, index) => (
            <StepCard
              key={index}
              step={step}
              inView={inView}
              delay={index * 0.15}
              isLast={index === steps.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const StepCard = ({ step, inView, delay, isLast }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.8, ease: 'easeOut' }}
      className={`relative p-10 border-t border-white/5 ${
        !isLast ? 'lg:border-r lg:border-white/5' : ''
      } group hover:bg-white/[0.02] transition-all duration-500`}
    >
      {/* Number */}
      <div className="mb-8">
        <span className="font-montserrat font-extralight text-6xl text-white/10 group-hover:text-[#C5A55C]/30 transition-colors duration-500">
          {step.number}
        </span>
      </div>
      
      {/* Content */}
      <div>
        <span className="font-montserrat font-light text-[10px] tracking-[0.25em] text-[#C5A55C] uppercase block mb-3">
          {step.subtitle}
        </span>
        <h3 className="font-montserrat font-light text-2xl text-white tracking-wide mb-4">
          {step.title}
        </h3>
        <p className="font-raleway font-light text-sm text-white/40 leading-relaxed tracking-wide">
          {step.description}
        </p>
      </div>
      
      {/* Gold accent on hover */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#C5A55C] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
    </motion.div>
  );
};

export default ProjectApproach;
